/**
 * Turns whatever query the agent wrote into cards. The shape of `data` is not known in advance
 * (any searchable field can be the root), so this reads fields by what they look like rather than
 * by name: a person's name becomes the title, contact details the subtitle, enum values
 * (CRITICALLY_LOW) chips, the first timestamp the date, and a few more fields the details.
 *
 * Records of the same patient (the same truentityId) become one card, so twenty alerts for three
 * patients read as three patients with their alerts.
 */

type Json = Record<string, unknown>;

export type TagTone = 'error' | 'warning' | 'default';

export type CardTag = { label: string; count: number; tone: TagTone };

export type CardDetail = { label: string; value: string };

export type ResultCard = {
  key: string;
  title: string;
  subtitle: string[];
  tags: CardTag[];
  /** Only for a card of one record: the details of several would disagree. */
  details: CardDetail[];
  /** ISO timestamp, the latest of the card's records. */
  date: string | null;
  /** Present when the card is (or belongs to) a patient, so it can open them. */
  truentityId: string | null;
  rpmStatus: string | null;
  records: number;
};

export type ResultCards = {
  cards: ResultCard[];
  /** Records shown, which cards may group. */
  shown: number;
  /** From `meta { totalCount }` when the query asked for it. */
  totalCount: number | null;
};

export const MAX_RECORDS = 25;
export const MAX_DETAILS = 3;
export const MAX_SUBTITLE = 3;
/** How far below a record objects are read: `account.user.firstName` is three levels. */
const MAX_PATH_DEPTH = 3;
const SKIPPED_KEYS = new Set(['__typename', 'id', 'meta']);

const NAME_KEYS = ['fullName', 'name', 'displayName', 'title', 'label'];
const CONTACT = /(phone|email|truentityId|dateOfBirth|mrn|ehrId)$/i;
/** CRITICALLY_LOW, ENROLLED: values from an enum, which read best as chips. */
const ENUM_VALUE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;
const MAX_DETAIL_LENGTH = 80;

type Field = { path: string; key: string; parent: string; value: unknown };

const isObject = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);

const isRecordList = (value: unknown): value is Json[] => Array.isArray(value) && value.length > 0 && value.every(isObject);

/** The first list of records at the root or up to two levels below it (`rpmAlerts`, `accounts.nodes`). */
const findCollection = (root: unknown): Json[] | null => {
  if (isRecordList(root)) return root;
  if (!isObject(root)) return null;

  const children = Object.entries(root).filter(([key]) => key !== 'meta');
  for (const [, value] of children) if (isRecordList(value)) return value;
  for (const [, value] of children) {
    if (!isObject(value)) continue;
    const nested = Object.values(value).find(isRecordList);
    if (nested) return nested;
  }
  return null;
};

/** Every scalar in the record, nested objects included; lists of records are left out. */
const fieldsOf = (record: Json, parent = '', depth = 1): Field[] =>
  Object.entries(record).flatMap(([key, value]) => {
    if (SKIPPED_KEYS.has(key)) return [];
    const path = parent ? `${parent}.${key}` : key;
    if (isObject(value)) return depth < MAX_PATH_DEPTH ? fieldsOf(value, path, depth + 1) : [];
    if (Array.isArray(value) && value.some(isObject)) return [];
    return [{ path, key, parent, value }];
  });

/** `firstName` -> `First name`, `CRITICALLY_LOW` -> `Critically low` */
export const humanize = (text: string): string => {
  const words = text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const text = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(String).join(', ') : null;
  return String(value);
};

/** Stringified JSON and long text say nothing at a glance. */
const readable = (value: string) => value.length <= MAX_DETAIL_LENGTH && !/^[[{]/.test(value.trim());

const toneOf = (value: string): TagTone => {
  if (/CRITICAL/.test(value)) return 'error';
  if (/(HIGH|LOW|FAIL|REJECT|OVERDUE|MISSED|EXPIRED)/.test(value)) return 'warning';
  return 'default';
};

const titleOf = (fields: Field[]): { title: string; used: Set<string> } => {
  const first = fields.find(field => field.key === 'firstName' && text(field.value));
  const last = fields.find(field => field.key === 'lastName' && field.parent === first?.parent && text(field.value));
  if (first) {
    const used = new Set([first.path, ...(last ? [last.path] : [])]);
    return { title: [text(first.value), last && text(last.value)].filter(Boolean).join(' '), used };
  }

  for (const key of NAME_KEYS) {
    const named = fields.find(field => field.key === key && typeof field.value === 'string' && field.value);
    if (named)
      return {
        title: ENUM_VALUE.test(String(named.value)) ? humanize(String(named.value)) : String(named.value),
        used: new Set([named.path])
      };
  }

  const anyText = fields.find(field => typeof field.value === 'string' && field.value && !ENUM_VALUE.test(field.value));
  return { title: anyText ? String(anyText.value) : '—', used: new Set(anyText ? [anyText.path] : []) };
};

type Reading = Omit<ResultCard, 'key' | 'records'> & { group: string | null };

const readRecord = (record: Json): Reading => {
  const fields = fieldsOf(record);
  const { title, used } = titleOf(fields);
  const take = (field: Field) => used.add(field.path);

  const truentityId = (fields.find(field => field.key === 'truentityId' && typeof field.value === 'string')?.value as string) ?? null;
  const rpmStatus = (fields.find(field => field.key === 'rpmStatus' && typeof field.value === 'string')?.value as string) ?? null;

  const subtitle = fields
    .filter(field => !used.has(field.path) && CONTACT.test(field.key) && text(field.value))
    .slice(0, MAX_SUBTITLE)
    .map(field => (take(field), text(field.value) as string));

  const dated = fields.find(
    field => !used.has(field.path) && !/birth/i.test(field.key) && typeof field.value === 'string' && TIMESTAMP.test(field.value)
  );
  if (dated) take(dated);

  const tags = fields
    .filter(field => !used.has(field.path) && typeof field.value === 'string' && ENUM_VALUE.test(field.value) && field.value.length > 2)
    .map(field => (take(field), field.value as string))
    .filter((value, index, all) => all.indexOf(value) === index)
    .map(value => ({ label: humanize(value), count: 1, tone: toneOf(value) }));

  const details = fields
    .filter(field => !used.has(field.path))
    .map(field => ({ label: humanize(field.key), value: text(field.value) }))
    .filter((detail): detail is CardDetail => detail.value !== null && readable(detail.value))
    .slice(0, MAX_DETAILS);

  return { title, subtitle, tags, details, date: (dated?.value as string) ?? null, truentityId, rpmStatus, group: truentityId };
};

const addTags = (tags: CardTag[], more: CardTag[]) =>
  more.reduce((merged, tag) => {
    const same = merged.find(existing => existing.label === tag.label);
    return same ? merged.map(existing => (existing === same ? { ...existing, count: existing.count + 1 } : existing)) : [...merged, tag];
  }, tags);

export const extractCards = (data: Json | null | undefined, rootField: string | null | undefined): ResultCards => {
  const root = data ? (rootField ? data[rootField] : Object.values(data)[0]) : null;
  const meta = isObject(root) && isObject(root.meta) ? root.meta : null;
  const totalCount = typeof meta?.totalCount === 'number' ? meta.totalCount : null;

  // A field that returns one record (not a list) is one card.
  const records = (findCollection(root) ?? (isObject(root) && !meta ? [root] : [])).slice(0, MAX_RECORDS);

  const cards = records.reduce<ResultCard[]>((all, record, index) => {
    const { group, ...reading } = readRecord(record);
    const existing = group ? all.find(card => card.truentityId === group) : undefined;
    if (!existing) return [...all, { ...reading, key: String(group ?? record.id ?? index), records: 1 }];

    const merged: ResultCard = {
      ...existing,
      tags: addTags(existing.tags, reading.tags),
      details: [],
      date: [existing.date, reading.date].filter(Boolean).sort().pop() ?? null,
      records: existing.records + 1
    };
    return all.map(card => (card === existing ? merged : card));
  }, []);

  return { cards, shown: records.length, totalCount };
};
